import * as THREE from 'three';

/**
 * Utility functions for perpendicular beam creation and validation
 */

export interface Node {
  id: string;
  x: number;
  y: number;
  z: number;
}

export interface Beam {
  id: string;
  startNodeId: string;
  endNodeId: string;
}

/**
 * Checks if a node is valid for perpendicular beam creation based on projection onto reference beam
 * @param targetNode The node to check
 * @param refStartNode Start node of reference beam
 * @param refEndNode End node of reference beam
 * @returns True if the node's projection falls within the beam segment (0 < t < 1)
 */
export const isValidForPerpBeam = (
  targetNode: Node,
  refStartNode: Node,
  refEndNode: Node
): boolean => {
  const beamStart = new THREE.Vector3(refStartNode.x, refStartNode.y, refStartNode.z);
  const beamEnd = new THREE.Vector3(refEndNode.x, refEndNode.y, refEndNode.z);
  const nodePoint = new THREE.Vector3(targetNode.x, targetNode.y, targetNode.z);
  
  // Project node onto beam line
  const beamDirection = beamEnd.clone().sub(beamStart);
  const nodeToStart = nodePoint.clone().sub(beamStart);
  const projection = nodeToStart.dot(beamDirection) / beamDirection.lengthSq();
  
  // Only valid if projection is strictly within the beam segment (0 < t < 1)
  return projection > 0 && projection < 1;
};

/**
 * Calculates the perpendicular point on a beam for a given node
 * @param targetNode The node to project
 * @param refStartNode Start node of reference beam
 * @param refEndNode End node of reference beam
 * @returns The perpendicular point coordinates and projection parameter
 */
export const calculatePerpPoint = (
  targetNode: Node,
  refStartNode: Node,
  refEndNode: Node
): { point: THREE.Vector3; parameter: number } => {
  const beamStart = new THREE.Vector3(refStartNode.x, refStartNode.y, refStartNode.z);
  const beamEnd = new THREE.Vector3(refEndNode.x, refEndNode.y, refEndNode.z);
  const nodePoint = new THREE.Vector3(targetNode.x, targetNode.y, targetNode.z);
  
  // Project node onto beam line
  const beamDirection = beamEnd.clone().sub(beamStart);
  const nodeToStart = nodePoint.clone().sub(beamStart);
  const projection = nodeToStart.dot(beamDirection) / beamDirection.lengthSq();
  const perpPoint = beamStart.clone().add(beamDirection.clone().multiplyScalar(projection));
  
  return {
    point: perpPoint,
    parameter: projection
  };
};

/**
 * Counts valid nodes for perpendicular beam creation
 * @param selectedNodeIds Array of selected node IDs
 * @param allNodes Array of all nodes
 * @param refBeam Reference beam
 * @returns Count of valid nodes
 */
export const countValidNodesForPerpBeam = (
  selectedNodeIds: string[],
  allNodes: Node[],
  refBeam: Beam
): number => {
  const refStartNode = allNodes.find(n => n.id === refBeam.startNodeId);
  const refEndNode = allNodes.find(n => n.id === refBeam.endNodeId);
  
  if (!refStartNode || !refEndNode) {
    return 0;
  }
  
  return selectedNodeIds.filter(nodeId => {
    const targetNode = allNodes.find(n => n.id === nodeId);
    if (!targetNode) return false;
    
    return isValidForPerpBeam(targetNode, refStartNode, refEndNode);
  }).length;
};

/**
 * Filters nodes to only return those valid for perpendicular beam creation
 * @param selectedNodeIds Array of selected node IDs
 * @param allNodes Array of all nodes
 * @param refBeam Reference beam
 * @returns Array of valid node IDs
 */
export const filterValidNodesForPerpBeam = (
  selectedNodeIds: string[],
  allNodes: Node[],
  refBeam: Beam
): string[] => {
  const refStartNode = allNodes.find(n => n.id === refBeam.startNodeId);
  const refEndNode = allNodes.find(n => n.id === refBeam.endNodeId);
  
  if (!refStartNode || !refEndNode) {
    return [];
  }
  
  return selectedNodeIds.filter(nodeId => {
    const targetNode = allNodes.find(n => n.id === nodeId);
    if (!targetNode) return false;
    
    return isValidForPerpBeam(targetNode, refStartNode, refEndNode);
  });
};

/**
 * Checks if a beam already exists between two nodes (in either direction)
 * @param nodeId1 First node ID
 * @param nodeId2 Second node ID
 * @param existingBeams Array of existing beams to check against
 * @returns True if a beam already exists between the nodes
 */
export const beamExistsBetweenNodes = (
  nodeId1: string,
  nodeId2: string,
  existingBeams: Beam[]
): boolean => {
  return existingBeams.some(beam => 
    (beam.startNodeId === nodeId1 && beam.endNodeId === nodeId2) ||
    (beam.startNodeId === nodeId2 && beam.endNodeId === nodeId1)
  );
};
